export const THREE_DS2_AUTHENTICATION_EXCEPTION = 'ThreeDs2_Authentication_Exception'
export const ATTEMPT_EXCEED_LIMITATION = 'Attempt_Exceed_Limitation'
export const PAYMENT_ERROR = 'Payment_Error'

export type PaymentPageErrorSource = 'callback' | 'error_handler'

export type PaymentPageErrorContext = {
  processor_response_reason: string
  processor_response_code: string
  error_source: PaymentPageErrorSource
  card_setup: boolean
}

type PaymentPageErrorMessages = {
  processor_declined_error: string
  attempts_exceeded_limit_error: string
  three_ds2_authentication_error?: string
  payment_page_token_refreshed_error?: string
}

type PaymentPageError = {
  key: string
  code: string
  message: string
  source: PaymentPageErrorSource
}

type PaymentPageErrorHandlerOptions = {
  container: HTMLElement
  refreshPaymentPage: () => Promise<void>
  sendErrorMessage: (key: string, message: string) => void
  trackError: (context: PaymentPageErrorContext) => void
}

export type PaymentPageErrorHandler = (error: PaymentPageError) => Promise<void>

function errorMessages(container: HTMLElement): PaymentPageErrorMessages {
  const value = container.getAttribute('data-error-messages')
  if (!value) throw new Error('Missing Zuora payment page error messages')
  return JSON.parse(value)
}

function normalizeErrorCode(code: string, message: string): string {
  if (code === THREE_DS2_AUTHENTICATION_EXCEPTION || code === ATTEMPT_EXCEED_LIMITATION || code === PAYMENT_ERROR) {
    return code
  }
  return message.match(/\[(.*?)\]/)?.[1] || code
}

function showPaymentError(container: HTMLElement, message: string): boolean {
  const banner = container.parentElement?.querySelector<HTMLElement>('.js-zuora-payment-error')
  const messageElement = banner?.querySelector<HTMLElement>('.js-zuora-payment-error-message')
  if (!banner || !messageElement) return false

  messageElement.textContent = message
  banner.hidden = false
  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
  banner.focus()
  return true
}

export function createPaymentPageErrorHandler({
  container,
  refreshPaymentPage,
  sendErrorMessage,
  trackError,
}: PaymentPageErrorHandlerOptions): PaymentPageErrorHandler {
  let currentRefresh: Promise<void> | undefined

  async function performRefresh(): Promise<void> {
    try {
      await refreshPaymentPage()
    } catch {
      const message = container.getAttribute('data-general-signature-error-message') || ''
      if (!showPaymentError(container, message)) sendErrorMessage('error', message)
    } finally {
      currentRefresh = undefined
    }
  }

  return async ({key, code, message, source}) => {
    if (key !== 'error') {
      sendErrorMessage(key, message)
      return
    }

    const errorCode = normalizeErrorCode(code, message)
    const refreshRequired =
      container.getAttribute('data-card-setup') === 'true' &&
      (errorCode === THREE_DS2_AUTHENTICATION_EXCEPTION ||
        errorCode === ATTEMPT_EXCEED_LIMITATION ||
        errorCode === PAYMENT_ERROR)

    if (refreshRequired && currentRefresh) {
      await currentRefresh
      return
    }

    trackError({
      processor_response_reason: message,
      processor_response_code: errorCode,
      error_source: source,
      card_setup: container.getAttribute('data-card-setup') === 'true',
    })

    const messages = errorMessages(container)
    let displayMessage = messages.processor_declined_error
    if (refreshRequired && errorCode === THREE_DS2_AUTHENTICATION_EXCEPTION) {
      displayMessage = messages.three_ds2_authentication_error || displayMessage
    } else if (refreshRequired && errorCode === ATTEMPT_EXCEED_LIMITATION) {
      displayMessage = messages.payment_page_token_refreshed_error || displayMessage
    } else if (errorCode === ATTEMPT_EXCEED_LIMITATION) {
      displayMessage = messages.attempts_exceeded_limit_error
    }

    if (source === 'error_handler' && !refreshRequired) {
      sendErrorMessage('error', displayMessage)
      return
    }

    if (!showPaymentError(container, displayMessage)) {
      sendErrorMessage('error', displayMessage)
    }

    if (refreshRequired) {
      currentRefresh = performRefresh()
      await currentRefresh
    }
  }
}
